import {
  Button,
  Flex,
  Paper,
  Text,
  Loader,
  Group,
  TextInput,
  Blockquote,
  Anchor,
} from "@mantine/core";
import { useAppDispatch, useAppSelector } from "../hooks";
import { RootState } from "../store";
import { displayServerErrorMessage } from "../shared/ErrorMessage";
import { initialize, updateField } from "../slices/form/createHost";
import { useEffect } from "react";
import {
  CreateHostRequest,
  useCreateHostMutation,
} from "../slices/api/ventsApi";
import { UserPermWithoutOwnerSegmentedControl } from "../shared/components/UserPermSegmentedControl";
import { IconInfo } from "../shared/components/Icons";
import { Perm } from "../slices/types";

interface Props {
  ventId: string;
  ventName: string;
  updateTime: string;
  userPerm: Perm;
  onClose: () => void;
}
const CreateHost = (props: Props) => {
  const { data, hasChanges } = useAppSelector(
    (state: RootState) => state.createHostForm
  );
  const [createHost] = useCreateHostMutation();

  const dispatch = useAppDispatch();
  useEffect(() => {
    dispatch(
      initialize({
        hostEmail: "",
        perm: "v",
      })
    );
  }, []);

  const handleSave = async () => {
    if (data === undefined) {
      console.error("undefined");
      return;
    }

    const request: CreateHostRequest = {
      ventId: props.ventId,
      hostEmail: data.hostEmail,
      perm: data.perm,
      updateTime: props.updateTime,
    };

    try {
      await createHost(request).unwrap();
      props.onClose();
    } catch (error) {
      displayServerErrorMessage(error);
    }
  };

  if (!data) {
    return <Loader color="blue" />;
  }

  return (
    <>
      <Paper>
        <Text size="lg">{`Add Host for ${props.ventName}`}</Text>
        <Text mt="xl">
          The host must already have an Early Bird account. If not they may
          register at{" "}
          <Anchor href="https://app.earlybirdrsvp.com" target="_blank">
            https://app.earlybirdrsvp.com.
          </Anchor>
        </Text>
        <Group mt="sm">
          <TextInput
            label={"Email"}
            disabled={props.userPerm !== "o"}
            required
            style={{ width: "300px" }}
            value={data.hostEmail ?? ""}
            onChange={(event) =>
              dispatch(
                updateField({
                  field: "hostEmail",
                  value: event.currentTarget.value,
                })
              )
            }
          />
        </Group>
        <Text mt="lg" size="sm" fw={500} mb={3}>
          Permission
        </Text>
        <UserPermWithoutOwnerSegmentedControl
          disabled={props.userPerm !== "o"}
          value={data.perm}
          onChange={(value) => {
            dispatch(
              updateField({
                field: "perm",
                value: value,
              })
            );
          }}
        />
        <Blockquote color="blue" icon={<IconInfo />} mt="xl">
          Viewers can view all event information. Managers can change all event
          information, except that they cannot add or change hosts. Owners can
          do everything that managers can do, but can also add and modify hosts.
          The host that creates an event is the sole owner, and that cannot be
          changed.
        </Blockquote>
        <Flex justify="flex-end">
          <Button mt="md" mr="md" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            mt="md"
            onClick={handleSave}
            disabled={!hasChanges || props.userPerm !== "o"}
          >
            Save
          </Button>
        </Flex>
      </Paper>
    </>
  );
};

export default CreateHost;
